import {
	addAssetDetailNote,
	checkAssetIds,
	checkAssetIdsRejected,
	checkAssetIdsResolved,
	getAssetCounts,
	getAssetCountsRejected,
	getAssetCountsResolved,
	getAssetDetail,
	getAssetDetailMapInsertResolved,
	getAssetDetailRejected,
	getAssetDetailResolved,
	getAssetLocation,
	loadAssetDetails,
	setAssetDetailIsWaiting,
	setAssetDetailNotesIsWaiting,
	setAssetLocation,
	setAssetLocationIsWaiting,
	setInspectionsValidatingAsset,
} from '@Actions/assetDetail.actions';
import { getAssetRisk, getAssetRiskHistory } from '@Actions/assetRisk.actions';
import { getAssetSummaryResolved } from '@Actions/assetSummary.actions';
import { getInspectionAttributes } from '@Actions/inspection.actions';
import {
	addAssetNoteApi,
	checkAssetIdsApi,
	getAssetCountsApi,
	getSummaryApi,
} from '@Services';
import {
	AssetAttrbuteField,
	AssetDetailParams,
	AssetDetailsSummary,
	AssetPipeInfoResolvedResponse,
} from '@Types/asset.types';
import { formatYearFieldValue, getMapLayerId } from '@Utils';
import { put, retry, takeLatest } from 'redux-saga/effects';

import { getAssetManhole } from '@Actions/assetManhole.actions';
import { getAssetPipe } from '@Actions/assetPipe.actions';
import {
	AssetCounts,
	AssetLocation,
	GetAssetLocationResponse,
	Note,
} from '@Types';
import { Geometry } from '@innovyze/shared-utils/';
import { PayloadAction } from '@reduxjs/toolkit';
import center from '@turf/center';
import { AxiosResponse } from 'axios';
import { AnyAction } from 'redux';

function* getAssetCountsSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<AssetCounts> = yield retry(
			5,
			15000,
			getAssetCountsApi,
			action.payload,
		);
		yield put(getAssetCountsResolved(payload.data));
	} catch (_e) {
		yield put(getAssetCountsRejected());
	}
}

function* watchGetAssetCounts() {
	yield takeLatest(getAssetCounts, getAssetCountsSaga);
}

function* getAssetDetailSaga(action: AnyAction) {
	try {
		yield put(setAssetDetailIsWaiting(!action.payload.forNotes));
		yield put(setAssetDetailNotesIsWaiting(action.payload.forNotes));

		const payload: AxiosResponse<AssetPipeInfoResolvedResponse> =
			yield retry(5, 30000, getSummaryApi, action.payload);

		// Year fields hold a year only - normalise them to a 4 digit year rather
		// than formatting them as dates, which the Properties panel would then
		// try to re-parse. (I360A-2587)
		// Hardcoded on purpose - unlike isYearField in the Properties panel,
		// this list won't auto-cover a new year* field added to the schema.
		if (payload.data.asset['yearLaid']) {
			payload.data.asset['yearLaid'] =
				formatYearFieldValue(
					payload.data.asset['yearLaid'] as string,
				) ?? '';
		}

		if (payload.data.asset['yearInstalled']) {
			payload.data.asset['yearInstalled'] =
				formatYearFieldValue(
					payload.data.asset['yearInstalled'] as string,
				) ?? '';
		}

		if (payload.data.asset['yearRenewed']) {
			payload.data.asset['yearRenewed'] =
				formatYearFieldValue(
					payload.data.asset['yearRenewed'] as string,
				) ?? '';
		}

		let attributes: AssetAttrbuteField[] = Object.keys(payload.data.asset)
			.filter(
				property =>
					property !== 'geometry' &&
					property !== 'segments' &&
					property !== 'tenantId' &&
					property !== 'notes',
			)
			.map(property => ({
				FieldName: property,
				FieldTitle: property,
				FieldValue:
					payload.data.asset[property] === null
						? ''
						: String(payload.data.asset[property]),
			}));

		if (
			payload.data.asset &&
			payload.data.asset.segments &&
			payload.data.asset.segments.length == 1
		) {
			attributes = [
				...attributes,
				...Object.keys(payload.data.asset.segments[0])
					.filter(
						property =>
							property !== 'startDistance' &&
							property !== 'segmentId' &&
							property !== 'startLength',
					)
					.map(property => ({
						FieldName: property,
						FieldTitle: property,
						FieldValue:
							payload.data.asset.segments[0][property] === null
								? ''
								: String(
										payload.data.asset.segments[0][
											property
										],
								  ),
					})),
			];
		}

		const result = {
			notes: payload.data.asset.notes as Note[],
			assetDetails: attributes,
		};

		yield put(getAssetDetailResolved(result));

		yield put(
			getAssetSummaryResolved({
				id: `${action.payload.assetType}#${action.payload.assetId}`,
				asset: payload.data.asset as AssetDetailsSummary,
			}),
		);

		if (payload.data.asset['geometry']) {
			let layerId = action.payload.layerId;
			const systemType = payload.data.asset['layerSystemType'] as string;
			if (layerId === undefined && systemType !== undefined) {
				layerId = getMapLayerId(action.payload.assetType, systemType);
			}

			yield put(
				getAssetDetailMapInsertResolved({
					geometry: payload.data.asset['geometry'] as Geometry,
					layerId: layerId,
				}),
			);
		}
	} catch (_e) {
		yield put(getAssetDetailRejected());
	}
}

function* watchGetAssetDetail() {
	yield takeLatest(getAssetDetail, getAssetDetailSaga);
}

function* addAssetDetailNoteSaga(action: AnyAction) {
	try {
		yield put(setAssetDetailNotesIsWaiting(true));
		yield retry(
			5,
			15000,
			addAssetNoteApi,
			action.payload.assetDetailParams,
			action.payload.addNoteParams,
		);
		yield put(getAssetDetail(action.payload.assetDetailParams));
	} catch (_e) {
		yield put(setAssetDetailNotesIsWaiting(false));
	}
}

function* watchAddAssetDetailNote() {
	yield takeLatest(addAssetDetailNote, addAssetDetailNoteSaga);
}

function* checkAssetIdsSaga(action: AnyAction) {
	try {
		yield put(setInspectionsValidatingAsset(true));
		const payload: AxiosResponse<string[]> = yield retry(
			5,
			15000,
			checkAssetIdsApi,
			action.payload,
		);
		yield put(checkAssetIdsResolved(payload.data));
		yield put(setInspectionsValidatingAsset(false));
	} catch (_e) {
		yield put(checkAssetIdsRejected());
		yield put(setInspectionsValidatingAsset(false));
	}
}

function* watchCheckAssetIds() {
	yield takeLatest(checkAssetIds, checkAssetIdsSaga);
}

// TODO make it generic @AssetSchemaMigration
function* loadAssetDetailsSaga(action: PayloadAction<AssetDetailParams>) {
	switch (action.payload.assetType) {
		case 'wwManhole':
			yield put(getAssetManhole(action.payload));
			break;
		default:
			yield put(getAssetPipe(action.payload));
			break;
	}

	yield put(getAssetDetail(action.payload));
	yield put(getAssetRiskHistory(action.payload));

	// const isWebSocketEnabled = useIsFeatureEnabled(
	// 	'info-360-asset-risk-web-socket',
	// );
	// const isWebSocketEnabled = true;
	// if (isWebSocketEnabled) {
	// 	const webSocket = WebSocketClass.getWebSockets();
	// 	webSocket.sendMessage(JSON.stringify(
	// 		{
	// 			assetId: `${action.payload.assetType}%23${action.payload.assetId}`,
	// 			action: 'riskSummary',
	// 		}
	// 	));
	// } else {
	// 	yield put(getRiskSummary(action.payload));
	// }

	// lib-am-common fetches its own risk and inspection data
	// (previously reused the property-pane cache from @innovyze/map-components)
	yield put(getAssetRisk(action.payload));
	yield put(getInspectionAttributes(action.payload));
}

function* watchGetMapAssetDetails() {
	yield takeLatest(loadAssetDetails, loadAssetDetailsSaga);
}

function* getAssetLocationSaga(action: AnyAction) {
	try {
		yield put(setAssetLocationIsWaiting(true));

		const payload: AxiosResponse<GetAssetLocationResponse> = yield retry(
			5,
			15000,
			getSummaryApi,
			action.payload,
		);

		const geometry = payload.data.asset.geometry;
		let result: AssetLocation | undefined = undefined;

		if (geometry) {
			let position: GeoJSON.Position | undefined = undefined;
			// Find the center
			switch (geometry.type) {
				case 'Point':
					position = (geometry as GeoJSON.Point).coordinates;
					break;
				case 'LineString':
					position = center(geometry as GeoJSON.LineString).geometry
						.coordinates;
					break;
				case 'Polygon':
					position = center(geometry as GeoJSON.Polygon).geometry
						.coordinates;
					break;
				case 'MultiPoint':
					position = center(geometry as GeoJSON.MultiPoint).geometry
						.coordinates;
					break;
				case 'MultiLineString':
					position = center(geometry as GeoJSON.MultiLineString)
						.geometry.coordinates;
					break;
				case 'MultiPolygon':
					position = center(geometry as GeoJSON.MultiPolygon).geometry
						.coordinates;
					break;
			}

			if (position) {
				result = {
					assetId: payload.data.asset.assetId,
					position,
				};
			}
		}

		yield put(setAssetLocation(result));
	} catch (_e) {
		yield put(setAssetLocation(undefined));
	}

	yield put(setAssetLocationIsWaiting(false));
}

function* watchGetAssetLocation() {
	yield takeLatest(getAssetLocation, getAssetLocationSaga);
}

export default [
	watchGetAssetDetail(),
	watchGetAssetCounts(),
	watchAddAssetDetailNote(),
	watchCheckAssetIds(),
	watchGetMapAssetDetails(),
	watchGetAssetLocation(),
];
